import React, { useState, useEffect, useRef, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Button,
    LinearProgress
} from '@mui/material';
import { ThemeContext } from 'styled-components';
import { getSessionDeadlineExpiration } from '../../../utils/auth/ClaimsParser';
import SectionHeader from '../../template/components/SectionHeader';

const SessionTimeoutModal = ({ onStayLoggedIn, onSessionTimeout, open }) => {
    const theme = useContext(ThemeContext);
    const [countdown, setCountdown] = useState(0);
    const [initialTimeRemaining, setInitialTimeRemaining] = useState(0);
    const countdownIntervalRef = useRef(null);

    useEffect(() => {
        if (open) {
            // Get the SessionDeadline expiration timestamp
            const deadlineExpiration = getSessionDeadlineExpiration();

            if (!deadlineExpiration) {
                onSessionTimeout();
                return;
            }

            // Calculate initial time remaining
            const calculateTimeRemaining = () => {
                const now = Date.now();
                const remaining = Math.max(0, Math.floor((deadlineExpiration - now) / 1000));
                return remaining;
            };

            const initialRemaining = calculateTimeRemaining();
            setInitialTimeRemaining(initialRemaining);
            setCountdown(initialRemaining);

            // Start countdown timer - recalculate based on actual deadline
            // Use a ref-like pattern by recalculating deadline on each tick
            const interval = setInterval(() => {
                // Re-fetch deadline in case it was updated
                const currentDeadline = getSessionDeadlineExpiration();
                if (!currentDeadline) {
                    clearInterval(interval);
                    onSessionTimeout();
                    return;
                }

                const now = Date.now();
                const remaining = Math.max(0, Math.floor((currentDeadline - now) / 1000));
                setCountdown(remaining);

                if (remaining <= 0) {
                    clearInterval(interval);
                    onSessionTimeout(); // Auto logout when countdown reaches 0
                }
            }, 1000);

            countdownIntervalRef.current = interval;
        } else {
            // Modal closed, clear countdown
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
            }
            setCountdown(0);
            setInitialTimeRemaining(0);
        }

        return () => {
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
            }
        };
    }, [open, onSessionTimeout]);

    const handleStayLoggedIn = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        onStayLoggedIn();
    };

    const handleLogout = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        onSessionTimeout();
    };

    return (
        <Dialog
            open={open}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: theme?.colors?.backgroundPrimary ?? theme?.colors?.primary,
                    color: theme?.colors?.text,
                    borderRadius: '16px',
                    maxWidth: { xs: '95vw', sm: '600px' },
                    width: { xs: '95vw', sm: '70vw' },
                }
            }}
        >
            <DialogTitle sx={{ padding: 0 }}>
                <SectionHeader
                    title="Still there?"
                    borderRadius="16px 16px 0 0"
                    margin={0}
                />
            </DialogTitle>

            <DialogContent sx={{
                paddingTop: 0,
                paddingLeft: 0,
                paddingRight: 0,
            }}>
                <Box sx={{ px: 2, pt: '32px' }}>
                    <LinearProgress
                        variant="determinate"
                        value={initialTimeRemaining > 0 ? (countdown / initialTimeRemaining) * 100 : 0}
                        sx={{
                            height: 24,
                            borderRadius: 2,
                            backgroundColor: theme?.colors?.border ? `${theme.colors.border}40` : 'rgba(0, 0, 0, 0.15)',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: theme?.colors?.brandSecondary || theme?.colors?.primary || 'primary.main',
                                borderRadius: 2
                            }
                        }}
                    />
                </Box>
            </DialogContent>

            <Box sx={{
                paddingTop: 3,
                paddingBottom: 3,
                paddingLeft: 0,
                paddingRight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}>
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    width: '100%',
                    px: 2,
                }}>
                    <Button
                        onClick={handleLogout}
                        fullWidth
                        variant="outlined"
                        sx={{
                            flex: 1,
                            borderRadius: '8px',
                            border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                            backgroundColor: 'transparent',
                            color: theme?.colors?.text,
                            '&:hover': {
                                borderColor: theme?.colors?.border || '#ccc',
                                backgroundColor: theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        Log Out
                    </Button>
                    <Button
                        onClick={handleStayLoggedIn}
                        fullWidth
                        variant="outlined"
                        sx={{
                            flex: 1,
                            borderRadius: '8px',
                            borderColor: theme?.current === 'light'
                                ? theme?.colors?.brandSecondary
                                : (theme?.colors?.secondary || theme?.colors?.brandSecondary),
                            color: '#ffffff',
                            backgroundColor: theme?.current === 'light'
                                ? theme?.colors?.brandSecondary
                                : (theme?.colors?.tableBackground || theme?.colors?.background),
                            '&:hover': {
                                borderColor: theme?.current === 'light'
                                    ? theme?.colors?.brandSecondary
                                    : (theme?.colors?.secondary || theme?.colors?.brandSecondary),
                                backgroundColor: theme?.current === 'light'
                                    ? theme?.colors?.brandSecondary
                                    : (theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)'),
                            },
                        }}
                    >
                        Stay Logged In
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default SessionTimeoutModal;
